import { useState } from "react";
import { Brain, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { useAuth } from "@/providers/AuthProvider";

type AnalysisEntityType = "job" | "candidate" | "match";

const ANALYSIS_CONFIG: Record<AnalysisEntityType, { analysisType: string; actionOnComplete: string }> = {
	job: { analysisType: "analyse-job-offer", actionOnComplete: "update-job" },
	candidate: { analysisType: "analyse-resume", actionOnComplete: "update-candidate" },
	match: { analysisType: "full-match", actionOnComplete: "update-match" },
};

interface SuperAdminReanalyzeButtonProps {
	entityType: AnalysisEntityType;
	entityId: string;
	variant?: "outline" | "ghost";
	className?: string;
	iconClassName?: string;
}

export const SuperAdminReanalyzeButton = ({
	entityType,
	entityId,
	variant = "outline",
	className,
	iconClassName = "h-4 w-4",
}: SuperAdminReanalyzeButtonProps) => {
	const { t } = useTranslation();
	const { isSuperAdmin } = useAuth();
	const [isLoading, setIsLoading] = useState(false);

	if (!isSuperAdmin || !entityId)
		return null;

	const handleClick = async () => {
		setIsLoading(true);
		try {
			const { error } = await supabase.functions.invoke("ai-processing?command=push-new-analysis", {
				method: "POST",
				body: {
					entityId,
					entityType,
					...ANALYSIS_CONFIG[entityType],
				},
			});
			if (error) throw error;
			toast({ title: t("common.success"), description: t("superAdmin.reanalyze.success") });
		} catch {
			toast({ title: t("common.error"), description: t("superAdmin.reanalyze.error"), variant: "destructive" });
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<Button
			type="button"
			variant={variant}
			size="icon"
			className={className}
			onClick={handleClick}
			disabled={isLoading}
			title={t("superAdmin.reanalyze.button")}
		>
			{isLoading ? <Loader2 className={`${iconClassName} animate-spin`} /> : <Brain className={iconClassName} />}
		</Button>
	);
};
